import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AdminPageHead } from '../components/AdminPageHead';
import {
    Box, Card, CardContent, Typography, Grid, Table, TableHead,
    TableRow, TableCell, TableBody, TableContainer, CircularProgress,
    TextField, Button, Chip, Divider, Tooltip, IconButton, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions, Collapse,
    Autocomplete, TablePagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/FilterAltOffOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import DeviceUnknownOutlinedIcon from '@mui/icons-material/DeviceUnknownOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { ZapFlowService } from '../../../../../core/services/zap-flow.service';
import { BASE_URL } from '../../../../../config/config';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

// ---------- Types ----------
interface Totals {
    total_triggers: number; sent_triggers: number; failed_triggers: number; pending_triggers: number;
    opened_triggers: number; total_links_sent: number; total_clicks: number; unique_clicks: number;
    bot_clicks: number; attributed_clicks: number;
}
interface AutomationCounts {
    total_users: number; total_automations: number;
    post_automations: number; story_automations: number; live_automations: number;
    total_rules: number; auto_reply_rules: number; zap_link_rules: number;
    rules_with_comment_reply: number; rules_with_dm_text: number; rules_with_zap_links: number;
}
interface UserBreakdownRow {
    influencer_id: number;
    influencer_name: string | null;
    influencer_email: string | null;
    influencer_phone: string | null;
    instagram_username: string | null;
    automation_count: number; post_count: number; story_count: number; live_count: number;
    rule_count: number; auto_reply_count: number; zap_link_count: number;
    total_triggers: number; total_opens: number; total_dms_sent: number;
    zap_triggers: number; zap_opens: number; zap_clicks: number;
}
interface InfluencerRow {
    influencer_id: number;
    influencer_name: string | null;
    influencer_email: string | null;
    influencer_phone: string | null;
    instagram_username: string | null;
    triggers: number; sent: number; opens: number; clicks: number; unique_clicks: number;
}
interface ProductRow {
    ig_zap_link_id: number; product_name: string; brand_name: string; short_link_code: string;
    product_image_url: string; manual_product_image_url?: string | null; product_url?: string | null;
    clicks: number; unique_clicks: number;
}
interface EventTypeRow { event_type: string; triggers: number; sent: number; opens: number; }
interface DeviceRow { device_type: 'mobile' | 'desktop' | 'tablet' | 'unknown'; clicks: number; unique_clicks: number; }
interface InfluencerOption {
    influencer_id: number;
    name: string | null;
    primary_email: string | null;
    primary_phone: string | null;
    instagram_username: string | null;
    instagram_display_name: string | null;
    automation_count: number;
}

interface AutomationRuleRow {
    ig_automation_rule_id: number; ig_automation_id: number; rule_type: 'auto_reply' | 'zap_link';
    status: string; is_active: number; trigger_count: number;
    keywords: string | null; zap_links_count: number;
    dm_triggers: number; dm_opens: number;
    zap_triggers: number; zap_opens: number; zap_clicks: number;
}
interface AutomationPostRow {
    ig_automation_post_id: number;
    media_id: string;
    media_product_type: string;
    media_type: string;
    thumbnail_url: string | null;
    media_url: string | null;
    gcp_media_file_url: string | null;
    permalink: string | null;
    shortcode: string | null;
    status?: string;
    is_active?: number;
}
interface AutomationDetailRow {
    ig_automation_id: number; automation_name: string;
    automation_media_type: 'post' | 'story' | 'live';
    status: string; is_active: number; trigger_count: number; created_at: string;
    rules: AutomationRuleRow[];
    posts?: AutomationPostRow[];
}
interface DmActivityRow {
    ig_automation_post_comment_id: number; ig_automation_rule_id: number; ig_automation_post_id?: number | null;
    from_user_id: string; from_username: string; keyword: string;
    message_id: string; recipient_id: string;
    triggered_at: string; opened_at: string | null;
    event_type: string; comment_text?: string | null; reply_text: string | null; message_text: string | null;
    comment_id?: string | null;
    post_thumbnail_url?: string | null;
    post_media_url?: string | null;
    post_gcp_url?: string | null;
    post_permalink?: string | null;
    post_media_product_type?: string | null;
    post_shortcode?: string | null;
}
// A product in the Zap Link carousel that was SENT to the user (the catalogue).
interface CatalogueItem {
    ig_automation_rule_id: number; ig_zap_link_id: number;
    product_name: string | null; brand_name: string | null; short_link_code: string;
    product_image_url?: string | null; manual_product_image_url?: string | null; product_url?: string | null;
    price?: string | null; discount_text?: string | null; sort_order?: number; status?: string;
}
interface ZapClickDetail {
    ig_zap_link_click_id: number; ig_zap_link_id: number; product_name: string;
    brand_name: string; short_link_code: string;
    product_image_url?: string | null;
    manual_product_image_url?: string | null;
    product_url?: string | null;
    device_type: string; os: string; browser: string; is_unique: number; clicked_at: string;
}
interface ZapActivityRow {
    ig_zap_link_trigger_id: number; ig_automation_rule_id: number; ig_automation_post_id?: number | null;
    ig_comment_id?: string | null;
    ig_user_id: string; ig_username: string; ig_recipient_id: string;
    trigger_keyword: string; event_type: string;
    meta_api_status: 'pending' | 'sent' | 'failed';
    links_sent_count: number;
    triggered_at: string; opened_at: string | null;
    ig_message_id: string;
    click_count: number; unique_count: number;
    clicks: ZapClickDetail[];
    catalogue?: CatalogueItem[];
    post_thumbnail_url?: string | null;
    post_media_url?: string | null;
    post_gcp_url?: string | null;
    post_permalink?: string | null;
    post_media_product_type?: string | null;
    post_shortcode?: string | null;
}

// One row per real interaction (a user's comment). Auto-reply (DM) and Zap Link
// (catalogue) activity for the SAME comment are merged via the IG comment id, so
// the shared columns aren't shown twice across two separate tables.
interface MergedActivityRow {
    key: string;
    comment_id: string | null;
    post: any;                 // carries post_* fields for PostThumbnail
    ig_username: string | null;
    ig_user_id: string;
    keyword: string;
    event_type: string;
    triggered_at: string;
    opened_at: string | null;
    dm?: DmActivityRow;        // auto-reply side (if any)
    zap?: ZapActivityRow;      // zap-link side (if any)
}

// ---------- Constants ----------
const FUNNEL_META = [
    { key: 'sent_triggers',   label: 'DMs Sent',     color: '#5c6bc0', icon: <SendOutlinedIcon fontSize="small" /> },
    { key: 'opened_triggers', label: 'DMs Opened',   color: '#26a69a', icon: <MarkEmailReadOutlinedIcon fontSize="small" /> },
    { key: 'total_clicks',    label: 'Total Clicks', color: '#ffa726', icon: <TouchAppOutlinedIcon fontSize="small" /> },
    { key: 'unique_clicks',   label: 'Unique Clicks', color: '#ef5350', icon: <BoltOutlinedIcon fontSize="small" /> },
] as const;
const EVENT_TYPE_LABEL: Record<string, string> = {
    comments: 'Comments', story_replies: 'Story Replies', live_comments: 'Live Comments',
};
const DEVICE_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    mobile:  { label: 'Mobile',  color: '#26a69a', icon: <PhoneIphoneOutlinedIcon fontSize="small" /> },
    desktop: { label: 'Desktop', color: '#5c6bc0', icon: <ComputerOutlinedIcon fontSize="small" /> },
    tablet:  { label: 'Tablet',  color: '#ffa726', icon: <TabletMacOutlinedIcon fontSize="small" /> },
    unknown: { label: 'Unknown', color: '#9e9e9e', icon: <DeviceUnknownOutlinedIcon fontSize="small" /> },
};

const fmtNum = (n: number | string | null | undefined) => Number(n || 0).toLocaleString();
const pct = (num: number, den: number) => (!den ? '—' : ((num / den) * 100).toFixed(1) + '%');
const fmtDateTime = (v: string | null) => v ? new Date(v).toLocaleString() : '—';
// YYYY-MM-DD (the <input type="date"> value) -> local short date, for the filter summary.
const fmtDate = (v: string) => v ? new Date(`${v}T00:00:00`).toLocaleDateString() : '';

// OLD: const today = new Date().toISOString().split('T')[0];
// toISOString() converts to UTC first, so for anyone ahead of UTC the "today"
// cap was yesterday for part of the day — e.g. at 02:28 IST on 20 Aug it
// returned 2026-08-19, making the 20th unselectable in the pickers.
// 'en-CA' formats the LOCAL date as YYYY-MM-DD, which is what the picker shows.
const today = new Date().toLocaleDateString('en-CA');
// OLD default range for the filter bar. The page now opens on all-time data,
// so this is no longer used — kept for reference.
// const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const EMPTY_TOTALS: Totals = {
    total_triggers: 0, sent_triggers: 0, failed_triggers: 0, pending_triggers: 0,
    opened_triggers: 0, total_links_sent: 0, total_clicks: 0, unique_clicks: 0,
    bot_clicks: 0, attributed_clicks: 0,
};
const EMPTY_COUNTS: AutomationCounts = {
    total_users: 0, total_automations: 0,
    post_automations: 0, story_automations: 0, live_automations: 0,
    total_rules: 0, auto_reply_rules: 0, zap_link_rules: 0,
    rules_with_comment_reply: 0, rules_with_dm_text: 0, rules_with_zap_links: 0,
};

// ---------- Small reusable: section header with info icon ----------
// Storefront / Link-in-Bio presence rows (admin overview of who published what)
interface StorefrontRow {
    storefront_id: number;
    influencer_id: number;
    store_name: string | null;
    store_slug: string | null;
    status: string | null;
    created_at: string | null;
    influencer_name: string | null;
    instagram_username: string | null;
    total_views: number;
    unique_visitors: number;
    total_clicks: number;
    product_count: number;
    ctr: number;
}

interface BioPageRow {
    bio_page_id: number;
    influencer_id: number;
    slug: string | null;
    display_name: string | null;
    status: string | null;
    created_at: string | null;
    influencer_name: string | null;
    instagram_username: string | null;
    total_views: number;
    unique_visitors: number;
    total_clicks: number;
    link_count: number;
    ctr: number;
}

const SectionHeader: React.FC<{ title: string; info: string }> = ({ title, info }) => (
    <Box display="flex" alignItems="center" gap={0.5} mb={1}>
        <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
        <Tooltip title={info} arrow placement="right">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
    </Box>
);

// ---------- Table column header with hover-tooltip description ----------
const ColHeader: React.FC<{ label: string; info: string; align?: 'left' | 'right' | 'center' }> = ({ label, info, align = 'left' }) => (
    <TableCell align={align}>
        <Tooltip title={info} arrow placement="top">
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.3,
                    cursor: 'help',
                    borderBottom: '1px dotted',
                    borderColor: 'text.disabled',
                    pb: '1px',
                }}
            >
                <strong>{label}</strong>
                <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            </Box>
        </Tooltip>
    </TableCell>
);

// ---------- URL helpers ----------
const igProfileUrl = (username: string | null | undefined): string | null => {
    if (!username) return null;
    const cleaned = String(username).replace(/^@+/, '').trim();
    if (!cleaned) return null;
    return `https://www.instagram.com/${encodeURIComponent(cleaned)}/`;
};

const shortLinkUrl = (code: string | null | undefined): string | null => {
    if (!code) return null;
    return `${BASE_URL}/zap/${code}`;
};

// Public URLs for an influencer's storefront / bio page. These pages are served by
// THIS app (see routes: /shop/:slug and /bio/:slug), so build them from the browser
// origin rather than the API BASE_URL.
const storefrontUrl = (slug: string | null | undefined): string | null =>
    slug ? `${window.location.origin}/shop/${slug}` : null;
const bioUrl = (slug: string | null | undefined): string | null =>
    slug ? `${window.location.origin}/bio/${slug}` : null;

// Best-available image for a product (prefers manual uploaded image)
const productImageSrc = (p: { product_image_url?: string | null; manual_product_image_url?: string | null }): string => {
    return p.manual_product_image_url || p.product_image_url || '';
};

// Best-available thumbnail for a post (prefers our GCP-uploaded copy)
const postThumbSrc = (p: { post_thumbnail_url?: string | null; post_media_url?: string | null; post_gcp_url?: string | null; thumbnail_url?: string | null; media_url?: string | null; gcp_media_file_url?: string | null }): string => {
    return p.post_gcp_url || p.gcp_media_file_url || p.post_thumbnail_url || p.thumbnail_url || p.post_media_url || p.media_url || '';
};

// ---------- Clickable @username → IG profile ----------
const IgUsernameLink: React.FC<{
    username: string | null | undefined;
    fallback?: string;
    bold?: boolean;
    variant?: 'body2' | 'caption';
    color?: string;
}> = ({ username, fallback = '—', bold = true, variant = 'body2', color }) => {
    const url = igProfileUrl(username);
    const display = username ? `@${String(username).replace(/^@+/, '')}` : fallback;
    if (!url) {
        return <Typography variant={variant} fontWeight={bold ? 500 : 400} component="span" sx={{ color }}>{display}</Typography>;
    }
    return (
        <Tooltip title="Open Instagram profile in new tab" arrow>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'none' }}
                onClick={(e) => e.stopPropagation()}
            >
                <Typography
                    variant={variant}
                    fontWeight={bold ? 500 : 400}
                    component="span"
                    sx={{
                        color: color || '#1976d2',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                        textUnderlineOffset: 2,
                        '&:hover': { textDecorationStyle: 'solid' },
                    }}
                >
                    {display}
                </Typography>
            </a>
        </Tooltip>
    );
};

// ---------- Clickable product (image+name → short_url, with full-name tooltip) ----------
const ProductLink: React.FC<{
    product: { product_name?: string | null; brand_name?: string | null; short_link_code?: string | null; product_image_url?: string | null; manual_product_image_url?: string | null; product_url?: string | null };
    nameMaxWidth?: number;
    avatarSize?: number;
    showBrand?: boolean;
}> = ({ product, nameMaxWidth = 240, avatarSize = 32, showBrand = false }) => {
    const href = shortLinkUrl(product.short_link_code) || product.product_url || '';
    const imgSrc = productImageSrc(product);
    const name = product.product_name || '—';

    const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
        href ? (
            <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
                {children}
            </a>
        ) : <>{children}</>
    );

    return (
        <Tooltip title={href ? `${name} — click to open the product page in a new tab` : name} arrow>
            <Box sx={{ cursor: href ? 'pointer' : 'default' }}>
                <Wrapper>
                    <Box display="flex" alignItems="center" gap={1}>
                        {imgSrc
                            ? <Avatar src={imgSrc} variant="rounded" sx={{ width: avatarSize, height: avatarSize }} />
                            : <Avatar variant="rounded" sx={{ width: avatarSize, height: avatarSize, fontSize: 12 }}>P</Avatar>
                        }
                        <Box display="flex" flexDirection="column">
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="body2" noWrap sx={{ maxWidth: nameMaxWidth, color: href ? '#1976d2' : 'inherit', textDecoration: href ? 'underline' : 'none', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>{name}</Typography>
                                {href && <OpenInNewIcon sx={{ fontSize: 12, color: 'text.disabled' }} />}
                            </Box>
                            {showBrand && product.brand_name && (
                                <Typography variant="caption" color="text.secondary" noWrap>{product.brand_name}</Typography>
                            )}
                        </Box>
                    </Box>
                </Wrapper>
            </Box>
        </Tooltip>
    );
};

// ---------- Clickable post/reel/story thumbnail with media-type badge ----------
const PostThumbnail: React.FC<{
    post: { post_thumbnail_url?: string | null; post_media_url?: string | null; post_gcp_url?: string | null; post_permalink?: string | null; post_media_product_type?: string | null; post_shortcode?: string | null; thumbnail_url?: string | null; media_url?: string | null; gcp_media_file_url?: string | null; permalink?: string | null; media_product_type?: string | null; shortcode?: string | null };
    size?: number;
    label?: string;
}> = ({ post, size = 48, label }) => {
    const src = postThumbSrc(post);
    const permalink = post.post_permalink || post.permalink || '';
    const mediaType = (post.post_media_product_type || post.media_product_type || '').toUpperCase();
    if (!src && !permalink) return null;

    const isReel = mediaType.includes('REEL');
    const badgeIcon = isReel ? <PlayCircleOutlineIcon sx={{ fontSize: 14 }} /> : <ImageOutlinedIcon sx={{ fontSize: 14 }} />;
    const badgeText = mediaType === 'REELS' ? 'REEL' : mediaType === 'FEED' ? 'POST' : mediaType === 'STORY' ? 'STORY' : (mediaType || 'POST');

    const inner = (
        <Box position="relative" sx={{ width: size, height: size, borderRadius: 1, overflow: 'hidden', background: '#f5f5f5', flexShrink: 0 }}>
            {src
                ? <img src={src} alt={label || 'post'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%', color: 'text.disabled' }}>{badgeIcon}</Box>
            }
            <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.55)', color: '#fff',
                fontSize: 9, fontWeight: 600,
                textAlign: 'center', py: 0.2,
                letterSpacing: 0.3,
            }}>
                {badgeText}
            </Box>
        </Box>
    );

    if (permalink) {
        return (
            <Tooltip title={`Open ${badgeText.toLowerCase()} on Instagram`} arrow>
                <a href={permalink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
                    {inner}
                </a>
            </Tooltip>
        );
    }
    return <Tooltip title={badgeText} arrow><Box display="inline-block">{inner}</Box></Tooltip>;
};

// ---------- Main Component ----------
const ZapFlowTrackingDashboard: React.FC = () => {
    const [loading, setLoading]       = useState(false);
    const [totals, setTotals]         = useState<Totals>(EMPTY_TOTALS);
    const [counts, setCounts]         = useState<AutomationCounts>(EMPTY_COUNTS);
    const [influencers, setInfluencers] = useState<InfluencerRow[]>([]);
    const [products, setProducts]     = useState<ProductRow[]>([]);
    const [eventTypes, setEventTypes] = useState<EventTypeRow[]>([]);
    const [devices, setDevices]       = useState<DeviceRow[]>([]);
    const [users, setUsers]           = useState<UserBreakdownRow[]>([]);
    const [storefronts, setStorefronts] = useState<StorefrontRow[]>([]);
    const [bioPages, setBioPages]       = useState<BioPageRow[]>([]);
    // OLD: defaulted to the last 30 days —
    //   const [startDate, setStartDate] = useState(thirtyDaysAgo);
    //   const [endDate, setEndDate]     = useState(today);
    // The page now opens on ALL-TIME data. Empty strings are stripped by
    // ZapFlowService, so no start_date/end_date reaches the API and the backend's
    // `hasDates` is false — i.e. no BETWEEN clause at all. A range is only applied
    // once the admin picks one and hits Apply.
    const [startDate, setStartDate]   = useState('');
    const [endDate, setEndDate]       = useState('');
    const [error, setError]           = useState('');

    // Influencer autocomplete search (replaces raw ID input)
    const [selectedInfluencer, setSelectedInfluencer] = useState<InfluencerOption | null>(null);
    const [influencerInput, setInfluencerInput]       = useState('');
    const [influencerOptions, setInfluencerOptions]   = useState<InfluencerOption[]>([]);
    const [influencerSearching, setInfluencerSearching] = useState(false);

    // Debounced search whenever input changes (min 2 chars)
    useEffect(() => {
        const q = influencerInput.trim();
        if (q.length < 2) { setInfluencerOptions([]); return; }
        let cancelled = false;
        setInfluencerSearching(true);
        const t = setTimeout(async () => {
            try {
                const res = await ZapFlowService.searchInfluencers(q);
                if (!cancelled) setInfluencerOptions(res?.data?.rows?.influencers || []);
            } catch {
                if (!cancelled) setInfluencerOptions([]);
            } finally {
                if (!cancelled) setInfluencerSearching(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [influencerInput]);

    // Drill-down modals
    const [userModal, setUserModal] = useState<{
        open: boolean;
        influencer_id: number | null;
        name: string | null;
        username: string | null;
    }>({ open: false, influencer_id: null, name: null, username: null });
    const [userModalLoading, setUserModalLoading] = useState(false);
    const [userAutomations, setUserAutomations] = useState<AutomationDetailRow[]>([]);

    const [trackingModal, setTrackingModal] = useState<{ open: boolean; ig_automation_id: number | null; automation_name: string }>({ open: false, ig_automation_id: null, automation_name: '' });
    const [trackingLoading, setTrackingLoading] = useState(false);
    const [trackingDm, setTrackingDm] = useState<DmActivityRow[]>([]);
    const [trackingZap, setTrackingZap] = useState<ZapActivityRow[]>([]);
    const [expandedTriggerId, setExpandedTriggerId] = useState<number | null>(null);
    const [expandedDmId, setExpandedDmId] = useState<number | null>(null);
    // Unified (merged) activity table
    const [expandedActivityKey, setExpandedActivityKey] = useState<string | null>(null);
    const [activityPage, setActivityPage] = useState(0);
    const [activityRpp, setActivityRpp] = useState(10);

    // ---------- Pagination state (client-side) ----------
    const [influencersPage, setInfluencersPage]       = useState(0);
    const [influencersRpp, setInfluencersRpp]         = useState(10);
    const [productsPage, setProductsPage]             = useState(0);
    const [productsRpp, setProductsRpp]               = useState(10);
    const [usersPage, setUsersPage]                   = useState(0);
    const [usersRpp, setUsersRpp]                     = useState(10);
    const [storefrontsPage, setStorefrontsPage]       = useState(0);
    const [storefrontsRpp, setStorefrontsRpp]         = useState(10);
    const [bioPage_, setBioPage_]                     = useState(0);
    const [bioRpp, setBioRpp]                         = useState(10);
    const [trackingDmPage, setTrackingDmPage]         = useState(0);
    const [trackingDmRpp, setTrackingDmRpp]           = useState(10);
    const [trackingZapPage, setTrackingZapPage]       = useState(0);
    const [trackingZapRpp, setTrackingZapRpp]         = useState(10);

    // Reset all paginations when underlying data refreshes
    useEffect(() => { setInfluencersPage(0); }, [influencers]);
    useEffect(() => { setProductsPage(0); }, [products]);
    useEffect(() => { setUsersPage(0); }, [users]);
    useEffect(() => { setStorefrontsPage(0); }, [storefronts]);
    useEffect(() => { setBioPage_(0); }, [bioPages]);
    useEffect(() => { setTrackingDmPage(0); }, [trackingDm]);
    useEffect(() => { setTrackingZapPage(0); }, [trackingZap]);
    useEffect(() => { setActivityPage(0); }, [trackingDm, trackingZap]);

    // `override` lets callers (Clear Filters) fetch with values that have not been
    // committed to state yet — setState is async, so reading state here right after
    // a reset would re-send the OLD filters and leave stale numbers on screen.
    const load = useCallback(async (override?: {
        start?: string;
        end?: string;
        influencer?: InfluencerOption | null;
    }) => {
        const effStart = override?.start !== undefined ? override.start : startDate;
        const effEnd   = override?.end   !== undefined ? override.end   : endDate;
        const effInf   = override && 'influencer' in override ? override.influencer : selectedInfluencer;

        setLoading(true); setError('');
        try {
            // If no influencer is selected, the backend returns data for ALL influencers (default)
            const res = await ZapFlowService.getZapFlowAnalytics({
                start_date: effStart,
                end_date: effEnd,
                influencer_id: effInf ? String(effInf.influencer_id) : '',
            });
            const data = res?.data?.rows;
            if (!data) { setError('No data returned'); return; }
            setTotals({ ...EMPTY_TOTALS, ...(data.totals || {}) });
            setCounts({ ...EMPTY_COUNTS, ...(data.automation_counts || {}) });
            setInfluencers(data.per_influencer || []);
            setProducts(data.top_products || []);
            setEventTypes(data.event_type || []);
            setDevices(data.device || []);
            setUsers(data.users_breakdown || []);
            setStorefronts(data.storefronts || []);
            setBioPages(data.bio_pages || []);
        } catch {
            setError('Failed to load zap flow analytics');
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, selectedInfluencer]);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    // ---------------------------------------------------------------
    // Filter validation + reset
    // ---------------------------------------------------------------
    // The backend only applies a range when BOTH dates are present
    // (`hasDates = !!(start_date && end_date)`), so a half-filled range would
    // silently fall back to all-time and look like "the filter did nothing".
    // Catch that here instead, and block impossible ranges outright.
    const dateError = useMemo(() => {
        const startOnly = !!startDate && !endDate;
        const endOnly   = !startDate && !!endDate;
        if (startOnly || endOnly) return 'Pick both From and To (or clear both for all-time).';
        if (startDate && endDate && startDate > endDate) return '"From" cannot be after "To".';
        // Belt-and-braces: the inputs also cap at today via their max attribute,
        // but a typed-in date can still get past that in some browsers.
        if (startDate > today || endDate > today) return 'Future dates are not allowed.';
        return '';
    }, [startDate, endDate]);

    const filtersActive = !!startDate || !!endDate || !!selectedInfluencer;

    // Reset to the page default (all-time, all influencers) and refetch straight
    // away with those cleared values.
    const clearFilters = useCallback(() => {
        setStartDate('');
        setEndDate('');
        setSelectedInfluencer(null);
        setInfluencerInput('');
        load({ start: '', end: '', influencer: null });
    }, [load]);


    // ---- Storefront / Link-in-Bio drill-down ----
    const [presenceModal, setPresenceModal] = useState<{
        open: boolean;
        influencer_id: number | null;
        name: string | null;
        username: string | null;
    }>({ open: false, influencer_id: null, name: null, username: null });
    const [presenceLoading, setPresenceLoading] = useState(false);
    const [presenceDetail, setPresenceDetail] = useState<{ storefront: any; bio: any }>({ storefront: null, bio: null });

    const closePresenceModal = () =>
        setPresenceModal({ open: false, influencer_id: null, name: null, username: null });

    const openPresenceDetail = async (inf_id: number, name: string | null, username: string | null) => {
        setPresenceModal({ open: true, influencer_id: inf_id, name, username });
        setPresenceLoading(true);
        setPresenceDetail({ storefront: null, bio: null });
        try {
            // Uses the COMMITTED filter dates so the drill-down matches the row it came from.
            const res = await ZapFlowService.getPresenceDetail({
                influencer_id: inf_id,
                start_date: startDate,
                end_date: endDate,
            });
            setPresenceDetail({
                storefront: res?.data?.rows?.storefront || null,
                bio: res?.data?.rows?.bio || null,
            });
        } catch { /* no-op */ }
        finally { setPresenceLoading(false); }
    };

    const openUserAutomations = async (inf_id: number, name: string | null = null, username: string | null = null) => {
        setUserModal({ open: true, influencer_id: inf_id, name, username });
        setUserModalLoading(true); setUserAutomations([]);
        try {
            const res = await ZapFlowService.getUserAutomations({ influencer_id: inf_id, start_date: startDate, end_date: endDate });
            setUserAutomations(res?.data?.rows?.automations || []);
        } catch { /* no-op */ }
        finally { setUserModalLoading(false); }
    };

    const openAutomationTracking = async (auto_id: number, name: string) => {
        setTrackingModal({ open: true, ig_automation_id: auto_id, automation_name: name });
        setTrackingLoading(true); setTrackingDm([]); setTrackingZap([]); setExpandedTriggerId(null); setExpandedDmId(null); setExpandedActivityKey(null); setActivityPage(0);
        try {
            const res = await ZapFlowService.getAutomationTracking({ ig_automation_id: auto_id, start_date: startDate, end_date: endDate });
            setTrackingDm(res?.data?.rows?.dm_activity || []);
            setTrackingZap(res?.data?.rows?.zap_activity || []);
        } catch { /* no-op */ }
        finally { setTrackingLoading(false); }
    };

    const sendRate    = pct(totals.sent_triggers, totals.total_triggers);
    const openRate    = pct(totals.opened_triggers, totals.sent_triggers);
    const clickRate   = pct(totals.total_clicks, totals.opened_triggers);
    const overallCTR  = pct(totals.total_clicks, totals.sent_triggers);

    const maxFunnel = Math.max(totals.sent_triggers, totals.opened_triggers, totals.total_clicks, totals.unique_clicks, 1);

    // Merge DM (auto-reply) + Zap (catalogue) activity into one row per comment.
    // Correlated on the IG comment id so the same interaction isn't shown twice.
    const mergedActivity = useMemo<MergedActivityRow[]>(() => {
        const byComment = new Map<string, MergedActivityRow>();
        const orphans: MergedActivityRow[] = [];

        trackingDm.forEach(d => {
            const cid = d.comment_id ? String(d.comment_id) : '';
            const row: MergedActivityRow = {
                key: cid ? `c-${cid}` : `dm-${d.ig_automation_post_comment_id}`,
                comment_id: cid || null,
                post: d, ig_username: d.from_username, ig_user_id: d.from_user_id,
                keyword: d.keyword, event_type: d.event_type,
                triggered_at: d.triggered_at, opened_at: d.opened_at, dm: d,
            };
            if (cid) byComment.set(cid, row); else orphans.push(row);
        });

        trackingZap.forEach(z => {
            const cid = z.ig_comment_id ? String(z.ig_comment_id) : '';
            if (cid && byComment.has(cid)) {
                const row = byComment.get(cid)!;
                row.zap = z;
                if (!row.opened_at && z.opened_at) row.opened_at = z.opened_at;
                if (!row.keyword && z.trigger_keyword) row.keyword = z.trigger_keyword;
            } else {
                const row: MergedActivityRow = {
                    key: cid ? `c-${cid}` : `zap-${z.ig_zap_link_trigger_id}`,
                    comment_id: cid || null,
                    post: z, ig_username: z.ig_username, ig_user_id: z.ig_user_id,
                    keyword: z.trigger_keyword, event_type: z.event_type,
                    triggered_at: z.triggered_at, opened_at: z.opened_at, zap: z,
                };
                if (cid) byComment.set(cid, row); else orphans.push(row);
            }
        });

        return [...Array.from(byComment.values()), ...orphans]
            .sort((a, b) => String(b.triggered_at || '').localeCompare(String(a.triggered_at || '')));
    }, [trackingDm, trackingZap]);

    // ---------- Render ----------
    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

            <AdminPageHead
                title="Zap Flow Tracking"
                subtitle="How creators' Instagram auto-replies perform — comments, replies sent, opens, and clicks on shoppable links. The filters below apply to everything."
                actions={
                    <Tooltip title="Refresh">
                        {/* OLD: onClick={load} — would pass the MouseEvent into load()'s override arg. */}
                        <IconButton onClick={() => load()} size="small" disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                    </Tooltip>
                }
            />

            {error && <Typography color="error" mb={2} variant="body2">{error}</Typography>}

            {/* ================= Filter Bar (now at TOP — applies to everything below) ================= */}
            <Card variant="outlined" sx={{ mb: 2, background: '#f9fafb' }}>
                <CardContent sx={{ py: '12px !important' }}>
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ mr: 1 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Filters
                            </Typography>
                            <Tooltip
                                title="Both filters apply to EVERY number on this page. With no dates set you're looking at all-time data. Set a range and each section narrows to it — the setup counts show what was created in that window, and the activity sections show sends, opens and clicks in it. Picking an influencer scopes the whole page to just their data."
                                arrow
                            >
                                <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                            </Tooltip>
                        </Box>

                        {/* `max` caps the native picker at today so future dates can't be
                            chosen; From is additionally capped at To (and vice versa) so the
                            range can never invert. Leaving both empty = all-time. */}
                        <TextField
                            type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
                            value={startDate} onChange={e => setStartDate(e.target.value)}
                            inputProps={{ max: endDate && endDate < today ? endDate : today }}
                            error={!!dateError}
                            sx={{ width: 155, background: '#fff' }}
                        />
                        <TextField
                            type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
                            value={endDate} onChange={e => setEndDate(e.target.value)}
                            inputProps={{ min: startDate || undefined, max: today }}
                            error={!!dateError}
                            sx={{ width: 155, background: '#fff' }}
                        />

                        <Autocomplete<InfluencerOption, false, false, false>
                            size="small"
                            sx={{ width: 360, background: '#fff' }}
                            value={selectedInfluencer}
                            onChange={(_, v) => setSelectedInfluencer(v)}
                            inputValue={influencerInput}
                            onInputChange={(_, v) => setInfluencerInput(v)}
                            options={influencerOptions}
                            loading={influencerSearching}
                            filterOptions={(x) => x /* server-side search; don't re-filter on client */}
                            isOptionEqualToValue={(o, v) => o.influencer_id === v.influencer_id}
                            getOptionLabel={(o) => {
                                if (!o) return '';
                                const handle = o.instagram_username ? `@${o.instagram_username}` : '';
                                const nm = o.name || o.instagram_display_name || '';
                                return [handle, nm].filter(Boolean).join(' · ') || `#${o.influencer_id}`;
                            }}
                            renderOption={(props, o) => (
                                <li {...props} key={o.influencer_id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body2" fontWeight={500}>
                                                {o.instagram_username ? `@${o.instagram_username}` : (o.name || `#${o.influencer_id}`)}
                                            </Typography>
                                            {o.automation_count > 0 && (
                                                <Chip label={`${o.automation_count} auto.`} size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                                            )}
                                            <Chip label={`#${o.influencer_id}`} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {[o.name, o.primary_email, o.primary_phone].filter(Boolean).join(' · ') || '—'}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Influencer"
                                    placeholder="@handle, name, email, or phone"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {influencerSearching ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            noOptionsText={influencerInput.trim().length < 2 ? 'Type at least 2 characters' : 'No matches'}
                            clearOnEscape
                        />

                        {/* OLD: onClick={load} — with load() now taking an optional override
                            arg, passing it directly would hand it the MouseEvent as `override`. */}
                        <Button variant="contained" size="small" onClick={() => load()} disabled={loading || !!dateError}>Apply</Button>

                        {filtersActive && (
                            <Button
                                variant="outlined" size="small" color="inherit"
                                onClick={clearFilters} disabled={loading}
                                startIcon={<ClearIcon fontSize="small" />}
                            >
                                Clear Filters
                            </Button>
                        )}

                        {selectedInfluencer && (
                            <Tooltip
                                title="Shows all activity for this person — both their own auto-reply setups AND any time they commented on someone else's post and got a reply."
                                arrow
                            >
                                <Chip
                                    label={`Scoped to @${selectedInfluencer.instagram_username || selectedInfluencer.name || selectedInfluencer.influencer_id} (owner + recipient)`}
                                    size="small"
                                    color="primary"
                                    // OLD: onDelete only reset the state, so the numbers on screen stayed
                                    // scoped to the removed influencer until Apply was pressed again.
                                    // Now it refetches immediately with the influencer dropped.
                                    onDelete={() => {
                                        setSelectedInfluencer(null);
                                        setInfluencerInput('');
                                        load({ influencer: null });
                                    }}
                                    variant="outlined"
                                />
                            </Tooltip>
                        )}

                        {/* Spells out exactly what the numbers below currently cover, so
                            "all time" is never mistaken for a filter that failed to apply. */}
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {startDate && endDate
                                ? `Activity from ${fmtDate(startDate)} to ${fmtDate(endDate)}`
                                : 'Showing all-time activity'}
                            {!selectedInfluencer && ' · all influencers'}
                        </Typography>

                        {dateError && (
                            <Typography variant="caption" color="error" sx={{ fontWeight: 500 }}>
                                {dateError}
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* ================= Zaps / Configured Automations Section ================= */}
            <SectionHeader
                // OLD: title="Total Zaps — Configured Automations (not date filtered)"
                // This section used to ignore the date range, so "Total" was accurate but
                // made the whole page look broken when the numbers never moved. The counts
                // are now filtered like everything else, so "Total" would be misleading —
                // these are the Zaps configured WITHIN the selected window.
                title="Zaps — Configured Automations"
                info="Auto-reply setups created in the selected date range. With no dates set this is every setup ever created (all-time). If you've picked an influencer above, you'll see only their setup."
            />
            <Grid container spacing={2} mb={2}>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <GroupOutlinedIcon fontSize="small" sx={{ color: '#7e57c2' }} />
                                <Typography variant="caption" color="text.secondary">Users</Typography>
                                <Tooltip title="Influencers who created at least one auto-reply setup in the selected date range (all-time when no dates are set)." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: '#7e57c2' }}>{loading ? '—' : fmtNum(counts.total_users)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <AutoAwesomeMosaicOutlinedIcon fontSize="small" sx={{ color: '#5c6bc0' }} />
                                <Typography variant="caption" color="text.secondary">Automations</Typography>
                                <Tooltip title="Auto-reply setups created in the selected date range (all-time when no dates are set). One influencer can create multiple — one per post, story, or live video." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: '#5c6bc0' }}>{loading ? '—' : fmtNum(counts.total_automations)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <RuleOutlinedIcon fontSize="small" sx={{ color: '#26a69a' }} />
                                <Typography variant="caption" color="text.secondary">Rules</Typography>
                                <Tooltip title="Keyword-response combinations created in the selected date range (all-time when no dates are set). Each setup can have multiple keywords, and each keyword has its own response (either a text reply or a shoppable product carousel)." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: '#26a69a' }}>{loading ? '—' : fmtNum(counts.total_rules)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <BoltOutlinedIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                                <Typography variant="caption" color="text.secondary">Zap Link Rules</Typography>
                                <Tooltip title="How many keyword responses are set up to send a shoppable product carousel (Zap Link) instead of a plain text reply." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: '#f59e0b' }}>{loading ? '—' : fmtNum(counts.zap_link_rules)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Automation type breakdown row */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined" sx={{ background: '#fafafa' }}>
                        <CardContent sx={{ py: '10px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                                <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                                <Typography variant="caption" color="text.secondary">Post / Comment Automations</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(counts.post_automations)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined" sx={{ background: '#fafafa' }}>
                        <CardContent sx={{ py: '10px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                                <AutoStoriesOutlinedIcon sx={{ fontSize: 14, color: '#d81b60' }} />
                                <Typography variant="caption" color="text.secondary">Story Automations</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(counts.story_automations)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined" sx={{ background: '#fafafa' }}>
                        <CardContent sx={{ py: '10px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                                <LiveTvOutlinedIcon sx={{ fontSize: 14, color: '#e64a19' }} />
                                <Typography variant="caption" color="text.secondary">Live Automations</Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(counts.live_automations)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined" sx={{ background: '#fafafa' }}>
                        <CardContent sx={{ py: '10px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                                <ForumOutlinedIcon sx={{ fontSize: 14, color: '#388e3c' }} />
                                <Typography variant="caption" color="text.secondary">Auto Reply Rules</Typography>
                                <Tooltip title="How many keyword responses are set up to reply with a plain text message (no shoppable products). These can either send a DM, reply publicly on the comment, or both." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(counts.auto_reply_rules)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Rule output capability row */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: '10px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" color="text.secondary">Rules with Comment Reply</Typography>
                                <Tooltip title="How many keyword responses also post a public reply on the commenter's original Instagram comment." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(counts.rules_with_comment_reply)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: '10px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" color="text.secondary">Rules with DM Text</Typography>
                                <Tooltip title="How many keyword responses send a text message in DM to the person who commented." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(counts.rules_with_dm_text)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent sx={{ py: '10px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography variant="caption" color="text.secondary">Rules with Product Links</Typography>
                                <Tooltip title="How many Zap Link responses have at least one product added to the carousel (ready to send to the user when triggered)." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(counts.rules_with_zap_links)}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* ================= Funnel Cards ================= */}
            <SectionHeader
                title="Zap Link Funnel — Trigger Activity"
                info="The Zap Link journey for the selected date range: how many times the auto-reply was triggered, how many product catalogue messages we delivered, how many people opened them, and how many clicked a product."
            />
            <Grid container spacing={2} mb={2}>
                {FUNNEL_META.map(m => {
                    const val = (totals as any)[m.key] || 0;
                    return (
                        <Grid item xs={6} md={3} key={m.key}>
                            <Card variant="outlined">
                                <CardContent sx={{ pb: '12px !important' }}>
                                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                        <Box sx={{ color: m.color, display: 'flex' }}>{m.icon}</Box>
                                        <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                                    </Box>
                                    {loading ? <CircularProgress size={18} /> : <Typography variant="h5" fontWeight={700} sx={{ color: m.color }}>{fmtNum(val)}</Typography>}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Status mini-cards */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={6} md={3}><Card variant="outlined" sx={{ background: '#fafafa' }}><CardContent sx={{ py: '10px !important' }}><Typography variant="caption" color="text.secondary">Total Triggers</Typography><Typography variant="h6" fontWeight={700}>{loading ? '—' : fmtNum(totals.total_triggers)}</Typography></CardContent></Card></Grid>
                <Grid item xs={6} md={3}><Card variant="outlined" sx={{ background: '#fff3e0' }}><CardContent sx={{ py: '10px !important' }}><Typography variant="caption" color="text.secondary">Pending</Typography><Typography variant="h6" fontWeight={700} sx={{ color: '#e65100' }}>{loading ? '—' : fmtNum(totals.pending_triggers)}</Typography></CardContent></Card></Grid>
                <Grid item xs={6} md={3}><Card variant="outlined" sx={{ background: '#ffebee' }}><CardContent sx={{ py: '10px !important' }}><Typography variant="caption" color="text.secondary">Failed Sends</Typography><Typography variant="h6" fontWeight={700} sx={{ color: '#c62828' }}>{loading ? '—' : fmtNum(totals.failed_triggers)}</Typography></CardContent></Card></Grid>
                <Grid item xs={6} md={3}><Card variant="outlined" sx={{ background: '#e8f5e9' }}><CardContent sx={{ py: '10px !important' }}><Typography variant="caption" color="text.secondary">Products Sent</Typography><Typography variant="h6" fontWeight={700} sx={{ color: '#2e7d32' }}>{loading ? '—' : fmtNum(totals.total_links_sent)}</Typography></CardContent></Card></Grid>
            </Grid>

            {/* Funnel bar */}
            <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                    <SectionHeader
                        title="Conversion Funnel"
                        info="The bar width shows the size of each stage compared to the biggest one. 'X% of prev' tells you how many people made it from the previous stage to this one."
                    />
                    <Box display="flex" gap={1} flexWrap="wrap">
                        {FUNNEL_META.map((m, i) => {
                            const val = (totals as any)[m.key] || 0;
                            const prevVal = i > 0 ? ((totals as any)[FUNNEL_META[i - 1].key] || 0) : 0;
                            const dropPct = i > 0 ? pct(val, prevVal) : null;
                            const barW = maxFunnel > 0 ? Math.max((val / maxFunnel) * 100, 3) : 3;
                            return (
                                <Box key={m.key} flex="1" minWidth={100}>
                                    <Box sx={{ height: 6, borderRadius: 3, background: m.color, width: `${barW}%`, mb: 0.5 }} />
                                    <Typography variant="caption" color="text.secondary" display="block">{m.label}</Typography>
                                    <Typography fontWeight={700} variant="body2">{fmtNum(val)}</Typography>
                                    {dropPct && <Box display="flex" alignItems="center" gap={0.3}><TrendingDownIcon sx={{ fontSize: 12, color: 'text.disabled' }} /><Typography variant="caption" color="text.disabled">{dropPct} of prev</Typography></Box>}
                                </Box>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>

            {/* Rate summary */}
            <SectionHeader
                title="Conversion Rates"
                info="Percentages showing how well the Zap Link flow is performing — what share of triggers turn into delivered messages, opened messages, and clicks."
            />
            <Grid container spacing={2} mb={2}>
                {[
                    { label: 'Send Success Rate', val: sendRate, tip: 'Of all the times the auto-reply fired, what percentage actually got delivered to the user on Instagram.' },
                    { label: 'DM Open Rate',      val: openRate, tip: 'Of all the delivered messages, what percentage the user opened and read.' },
                    { label: 'Open → Click Rate', val: clickRate, tip: 'Of all the people who opened the message, what percentage clicked on a product.' },
                    { label: 'Overall CTR',       val: overallCTR, tip: 'The full picture: of all the delivered messages, what percentage led to a product click. The end-to-end success rate.' },
                ].map(item => (
                    <Grid item xs={6} md={3} key={item.label}>
                        <Card variant="outlined" sx={{ background: '#fafafa' }}>
                            <CardContent sx={{ py: '10px !important' }}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                    <Tooltip title={item.tip} arrow><InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} /></Tooltip>
                                </Box>
                                <Typography variant="h6" fontWeight={700}>{loading ? '—' : item.val}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* ================= Per-Influencer (zap-link) + Trigger source + Devices ================= */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} lg={8}>
                    <SectionHeader
                        title="Per-Influencer Zap Link Activity"
                        info="One row per influencer whose Zap Link auto-replies fired in the selected date range. Shows how many times each influencer's Zap Link triggered, how many messages were delivered, opened, and clicked."
                    />
                    <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Table size="small" sx={{ minWidth: 600 }}>
                            <TableHead>
                                <TableRow sx={{ background: '#f5f5f5' }}>
                                    <ColHeader label="Influencer" info="The influencer who set up the Zap Link auto-reply. Click their @handle to open their Instagram profile in a new tab." />
                                    <ColHeader label="Triggers" align="right" info="How many times the Zap Link auto-reply fired — i.e. how many people commented with a matching keyword and we tried to send them the product carousel." />
                                    <ColHeader label="Sent" align="right" info="Of those triggers, how many product carousel messages were actually delivered to the user on Instagram." />
                                    <ColHeader label="Opens" align="right" info="Of those delivered messages, how many the user actually opened and read." />
                                    <ColHeader label="Clicks" align="right" info="Total times a user clicked on any product in the carousel. Counts every click, including the same user clicking again." />
                                    <ColHeader label="Unique" align="right" info="How many different people clicked. If the same person clicked 5 times, that counts as 1 here." />
                                    <ColHeader label="CTR" align="right" info="Click-Through Rate — out of the messages we delivered, what percentage led to a product click. Higher = the catalogue is working better." />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                                ) : influencers.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No zap triggers in this period.</TableCell></TableRow>
                                ) : influencers.slice(influencersPage * influencersRpp, influencersPage * influencersRpp + influencersRpp).map(r => {
                                    const tooltipContent = (
                                        <Box sx={{ p: 0.5 }}>
                                            <Typography variant="caption" display="block" fontWeight={600}>{r.influencer_name || '— no name —'}</Typography>
                                            <Typography variant="caption" display="block">ID: <span style={{ fontFamily: 'monospace' }}>#{r.influencer_id}</span></Typography>
                                            {r.instagram_username && <Typography variant="caption" display="block">IG: @{r.instagram_username} (click to open profile)</Typography>}
                                            {r.influencer_email && <Typography variant="caption" display="block">Email: {r.influencer_email}</Typography>}
                                            {r.influencer_phone && <Typography variant="caption" display="block">Phone: {r.influencer_phone}</Typography>}
                                        </Box>
                                    );
                                    return (
                                    <TableRow key={r.influencer_id} hover>
                                        <TableCell>
                                            <Tooltip title={tooltipContent} arrow placement="right">
                                                <Box sx={{ display: 'inline-flex', flexDirection: 'column', cursor: 'help', maxWidth: 200 }}>
                                                    {r.influencer_name
                                                        ? <Typography variant="body2" fontWeight={500} noWrap>{r.influencer_name}</Typography>
                                                        : <IgUsernameLink username={r.instagram_username} fallback={`#${r.influencer_id}`} variant="body2" bold />
                                                    }
                                                    {r.instagram_username && r.influencer_name && (
                                                        <IgUsernameLink username={r.instagram_username} variant="caption" bold={false} />
                                                    )}
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">{fmtNum(r.triggers)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.sent)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.opens)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.clicks)}</TableCell>
                                        <TableCell align="right">{fmtNum(r.unique_clicks)}</TableCell>
                                        <TableCell align="right"><Typography variant="body2" fontWeight={600} sx={{ color: Number(r.clicks) > 0 ? '#2e7d32' : 'text.secondary' }}>{pct(Number(r.clicks), Number(r.sent))}</Typography></TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        {influencers.length > 0 && (
                            <TablePagination
                                component="div"
                                count={influencers.length}
                                page={influencersPage}
                                onPageChange={(_, p) => setInfluencersPage(p)}
                                rowsPerPage={influencersRpp}
                                onRowsPerPageChange={(e) => { setInfluencersRpp(parseInt(e.target.value, 10)); setInfluencersPage(0); }}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        )}
                    </TableContainer>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <SectionHeader
                        title="By Trigger Source"
                        info="Where the auto-reply was triggered from on Instagram — a comment on a post, a reply to a story, or a comment during a live stream. T = how many times it fired. S = how many messages were delivered. O = how many were opened."
                    />
                    <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent sx={{ pb: '12px !important' }}>
                            {loading ? <CircularProgress size={20} /> : eventTypes.length === 0 ? <Typography variant="caption" color="text.secondary">No data</Typography> : eventTypes.map(e => (
                                <Box key={e.event_type} display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
                                    <Typography variant="body2">{EVENT_TYPE_LABEL[e.event_type] || e.event_type}</Typography>
                                    <Box display="flex" gap={1.5}>
                                        <Tooltip title="Triggered — how many times someone's comment matched a keyword and we tried to send the product catalogue." arrow>
                                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>T: {fmtNum(e.triggers)}</Typography>
                                        </Tooltip>
                                        <Tooltip title="Sent — how many of those messages were successfully delivered to the user on Instagram." arrow>
                                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>S: {fmtNum(e.sent)}</Typography>
                                        </Tooltip>
                                        <Tooltip title="Opened — how many delivered messages the user actually opened and read." arrow>
                                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>O: {fmtNum(e.opens)}</Typography>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>

                    <SectionHeader title="Clicks By Device" info="What kind of device people used when they clicked a product link — mobile, desktop, or tablet." />
                    <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            {loading ? <CircularProgress size={20} /> : devices.length === 0 ? <Typography variant="caption" color="text.secondary">No clicks yet</Typography> : devices.map(d => {
                                const meta = DEVICE_META[d.device_type] || DEVICE_META.unknown;
                                const totalDeviceClicks = devices.reduce((s, x) => s + Number(x.clicks), 0);
                                const sharePct = totalDeviceClicks ? ((Number(d.clicks) / totalDeviceClicks) * 100).toFixed(0) : '0';
                                return (
                                    <Box key={d.device_type} display="flex" alignItems="center" gap={1} mb={0.8}>
                                        <Box sx={{ color: meta.color, display: 'flex' }}>{meta.icon}</Box>
                                        <Typography variant="body2" sx={{ flex: 1 }}>{meta.label}</Typography>
                                        <Typography variant="caption" color="text.secondary">{fmtNum(d.clicks)} ({sharePct}%)</Typography>
                                    </Box>
                                );
                            })}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* ================= Top Products ================= */}
            <SectionHeader title="Top Clicked Products" info="The most-clicked products from Zap Link carousels in the selected date range." />
            <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, mb: 3 }}>
                <Table size="small" sx={{ minWidth: 700 }}>
                    <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                            <ColHeader label="Product" info="Product name and thumbnail. Click to open the product page in a new tab." />
                            <ColHeader label="Brand" info="The brand or website where the product is sold." />
                            <ColHeader label="Short Code" info="The short link ID we use to track clicks for this product. Click it to open the product page in a new tab." />
                            <ColHeader label="Clicks" align="right" info="Total times this product was clicked, counting every click — including the same person clicking multiple times." />
                            <ColHeader label="Unique" align="right" info="How many different people clicked. If the same person clicked 5 times, that counts as 1 here." />
                            <ColHeader label="Repeat Rate" align="right" info="How often people came back to click again. A higher number means the product caught their interest enough to revisit." />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                        ) : products.length === 0 ? (
                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No product clicks in this period.</TableCell></TableRow>
                        ) : products.slice(productsPage * productsRpp, productsPage * productsRpp + productsRpp).map(p => {
                            const repeatPct = Number(p.clicks) > 0 ? (((Number(p.clicks) - Number(p.unique_clicks)) / Number(p.clicks)) * 100).toFixed(1) + '%' : '—';
                            const shortHref = shortLinkUrl(p.short_link_code);
                            return (
                                <TableRow key={p.ig_zap_link_id} hover>
                                    <TableCell><ProductLink product={p} nameMaxWidth={240} avatarSize={32} /></TableCell>
                                    <TableCell><Typography variant="caption" color="text.secondary">{p.brand_name || '—'}</Typography></TableCell>
                                    <TableCell>
                                        {shortHref ? (
                                            <Tooltip title={`Open short link in new tab — ${shortHref}`} arrow>
                                                <a href={shortHref} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                    <Chip label={p.short_link_code} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11, cursor: 'pointer', '&:hover': { background: '#f0f7ff' } }} clickable />
                                                </a>
                                            </Tooltip>
                                        ) : (
                                            <Chip label={p.short_link_code} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{fmtNum(p.clicks)}</TableCell>
                                    <TableCell align="right">{fmtNum(p.unique_clicks)}</TableCell>
                                    <TableCell align="right"><Typography variant="body2" color="text.secondary">{repeatPct}</Typography></TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {products.length > 0 && (
                    <TablePagination
                        component="div"
                        count={products.length}
                        page={productsPage}
                        onPageChange={(_, p) => setProductsPage(p)}
                        rowsPerPage={productsRpp}
                        onRowsPerPageChange={(e) => { setProductsRpp(parseInt(e.target.value, 10)); setProductsPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                )}
            </TableContainer>

            <Divider sx={{ mb: 2 }} />

            {/* ================= Storefront & Link in Bio ================= */}
            <SectionHeader
                title="Storefront & Link in Bio — Published Pages"
                info="Every influencer who has published a Zap Storefront or a Link-in-Bio page, with the public URL and its traffic. Pages are listed by when they were created (all-time when no dates are set); views and clicks are counted within the selected range. Click the eye icon for the full breakdown."
            />

            <Grid container spacing={2} mb={2}>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <StorefrontOutlinedIcon fontSize="small" sx={{ color: '#ec407a' }} />
                                <Typography variant="caption" color="text.secondary">Storefronts</Typography>
                                <Tooltip title="How many influencers have published a Zap Storefront (their own shoppable product page)." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: '#ec407a' }}>
                                {loading ? '—' : fmtNum(storefronts.length)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card variant="outlined">
                        <CardContent sx={{ pb: '12px !important' }}>
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                <LinkOutlinedIcon fontSize="small" sx={{ color: '#42a5f5' }} />
                                <Typography variant="caption" color="text.secondary">Link in Bio Pages</Typography>
                                <Tooltip title="How many influencers have published a Link-in-Bio page (their own link hub for their Instagram bio)." arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: '#42a5f5' }}>
                                {loading ? '—' : fmtNum(bioPages.length)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ---- Storefronts table ---- */}
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                STOREFRONTS
            </Typography>
            <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                <Table size="small" sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                            <ColHeader label="Influencer" info="The influencer who owns this storefront." />
                            <ColHeader label="Store" info="The store's display name and its public URL. Click to open the live store in a new tab." />
                            <ColHeader label="Status" info="Active stores are live and publicly reachable. Draft stores have been created but not published yet." />
                            <ColHeader label="Products" align="right" info="How many active products are listed in the store right now." />
                            <ColHeader label="Views" align="right" info="Total page views in the selected date range, counting repeat visits." />
                            <ColHeader label="Visitors" align="right" info="How many different people viewed the store in the selected date range." />
                            <ColHeader label="Clicks" align="right" info="Outbound product clicks from the store in the selected date range." />
                            <ColHeader label="CTR" align="right" info="Clicks divided by views — how often a visit turned into a product click." />
                            <ColHeader label="Created" info="When the influencer created this storefront." />
                            <ColHeader label="Details" align="center" info="Open the full analytics for this influencer's storefront and bio page." />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                        ) : storefronts.length === 0 ? (
                            <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No storefronts created in this period.</TableCell></TableRow>
                        ) : storefronts.slice(storefrontsPage * storefrontsRpp, storefrontsPage * storefrontsRpp + storefrontsRpp).map(sf => {
                            const href = storefrontUrl(sf.store_slug);
                            return (
                                <TableRow key={sf.storefront_id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={500}>{sf.influencer_name || `#${sf.influencer_id}`}</Typography>
                                        {sf.instagram_username && (
                                            <Typography variant="caption" color="primary">@{sf.instagram_username}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{sf.store_name || '—'}</Typography>
                                        {href && (
                                            <Tooltip title={`Open the live store in a new tab — ${href}`} arrow>
                                                <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                    <Typography variant="caption" sx={{ color: '#1976d2', wordBreak: 'break-all' }}>/shop/{sf.store_slug}</Typography>
                                                </a>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={sf.status || '—'}
                                            size="small"
                                            color={sf.status === 'active' ? 'success' : 'default'}
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: 11, textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{fmtNum(sf.product_count)}</TableCell>
                                    <TableCell align="right">{fmtNum(sf.total_views)}</TableCell>
                                    <TableCell align="right">{fmtNum(sf.unique_visitors)}</TableCell>
                                    <TableCell align="right">{fmtNum(sf.total_clicks)}</TableCell>
                                    <TableCell align="right"><Typography variant="body2" color="text.secondary">{sf.ctr}%</Typography></TableCell>
                                    <TableCell><Typography variant="caption" color="text.secondary">{fmtDateTime(sf.created_at)}</Typography></TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View full analytics" arrow>
                                            <IconButton size="small" onClick={() => openPresenceDetail(sf.influencer_id, sf.influencer_name, sf.instagram_username)}>
                                                <VisibilityOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {storefronts.length > 0 && (
                    <TablePagination
                        component="div"
                        count={storefronts.length}
                        page={storefrontsPage}
                        onPageChange={(_, pg) => setStorefrontsPage(pg)}
                        rowsPerPage={storefrontsRpp}
                        onRowsPerPageChange={(e) => { setStorefrontsRpp(parseInt(e.target.value, 10)); setStorefrontsPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                )}
            </TableContainer>

            {/* ---- Link in Bio table ---- */}
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                LINK IN BIO
            </Typography>
            <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small" sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                            <ColHeader label="Influencer" info="The influencer who owns this Link-in-Bio page." />
                            <ColHeader label="Page" info="The page's display name and its public URL. Click to open the live page in a new tab." />
                            <ColHeader label="Status" info="Active pages are live and publicly reachable. Draft pages have been created but not published yet." />
                            <ColHeader label="Links" align="right" info="How many active links are on the page right now." />
                            <ColHeader label="Views" align="right" info="Total page views in the selected date range, counting repeat visits." />
                            <ColHeader label="Visitors" align="right" info="How many different people viewed the page in the selected date range." />
                            <ColHeader label="Clicks" align="right" info="Outbound link clicks from the page in the selected date range." />
                            <ColHeader label="CTR" align="right" info="Clicks divided by views — how often a visit turned into a link click." />
                            <ColHeader label="Created" info="When the influencer created this page." />
                            <ColHeader label="Details" align="center" info="Open the full analytics for this influencer's storefront and bio page." />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                        ) : bioPages.length === 0 ? (
                            <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No Link-in-Bio pages created in this period.</TableCell></TableRow>
                        ) : bioPages.slice(bioPage_ * bioRpp, bioPage_ * bioRpp + bioRpp).map(bp => {
                            const href = bioUrl(bp.slug);
                            return (
                                <TableRow key={bp.bio_page_id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={500}>{bp.influencer_name || `#${bp.influencer_id}`}</Typography>
                                        {bp.instagram_username && (
                                            <Typography variant="caption" color="primary">@{bp.instagram_username}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{bp.display_name || '—'}</Typography>
                                        {href && (
                                            <Tooltip title={`Open the live page in a new tab — ${href}`} arrow>
                                                <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                    <Typography variant="caption" sx={{ color: '#1976d2', wordBreak: 'break-all' }}>/bio/{bp.slug}</Typography>
                                                </a>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={bp.status || '—'}
                                            size="small"
                                            color={bp.status === 'active' ? 'success' : 'default'}
                                            variant="outlined"
                                            sx={{ height: 20, fontSize: 11, textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{fmtNum(bp.link_count)}</TableCell>
                                    <TableCell align="right">{fmtNum(bp.total_views)}</TableCell>
                                    <TableCell align="right">{fmtNum(bp.unique_visitors)}</TableCell>
                                    <TableCell align="right">{fmtNum(bp.total_clicks)}</TableCell>
                                    <TableCell align="right"><Typography variant="body2" color="text.secondary">{bp.ctr}%</Typography></TableCell>
                                    <TableCell><Typography variant="caption" color="text.secondary">{fmtDateTime(bp.created_at)}</Typography></TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View full analytics" arrow>
                                            <IconButton size="small" onClick={() => openPresenceDetail(bp.influencer_id, bp.influencer_name, bp.instagram_username)}>
                                                <VisibilityOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {bioPages.length > 0 && (
                    <TablePagination
                        component="div"
                        count={bioPages.length}
                        page={bioPage_}
                        onPageChange={(_, pg) => setBioPage_(pg)}
                        rowsPerPage={bioRpp}
                        onRowsPerPageChange={(e) => { setBioRpp(parseInt(e.target.value, 10)); setBioPage_(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                )}
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            {/* ================= User Activity Breakdown (drill-down) ================= */}
            <SectionHeader
                title="User Activity — All Automation Types"
                info="Every influencer who has at least one auto-reply set up. The 'Automations / Post / Story / Live / Auto-Reply / Zap-Link' columns show what's currently set up. The 'DM Sent / Opened / Zap Clicks' columns show what actually happened in the selected date range. Click the eye icon to drill into their setups."
            />
            <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                            <ColHeader label="Influencer" info="The influencer who set up the auto-replies. Click their @handle to open their Instagram profile in a new tab. Click the eye icon to drill into their setups and see exactly who interacted." />
                            <ColHeader label="Automations" align="right" info="How many auto-reply setups this influencer has created in total (across posts, stories, and live videos)." />
                            <ColHeader label="Post" align="right" info="Auto-replies set up on Instagram posts. Fire when someone comments on the post with a matching keyword." />
                            <ColHeader label="Story" align="right" info="Auto-replies set up on Instagram stories. Fire when someone replies to the story with a matching keyword." />
                            <ColHeader label="Live" align="right" info="Auto-replies set up on Instagram live videos. Fire when someone comments during the live with a matching keyword." />
                            <ColHeader label="Auto-Reply" align="right" info="How many of the influencer's keyword responses send a plain text reply (a DM message and/or a public comment reply) — no product carousel. This shows what's set up, NOT how many were sent." />
                            <ColHeader label="Zap-Link" align="right" info="How many of the influencer's keyword responses send a shoppable product carousel (Zap Link) instead of plain text. This also shows what's set up, NOT how many were sent." />
                            <ColHeader label="DM Sent" align="right" info="How many DMs actually went out to people in the selected date range. Counts BOTH plain text DMs and product carousel DMs combined." />
                            <ColHeader label="DM Opened" align="right" info="Of those delivered DMs, how many the recipients actually opened and read." />
                            <ColHeader label="Zap Clicks" align="right" info="Total times people clicked on a product in the influencer's product carousels in the date range. Only applies when the response is a Zap Link, not a plain text reply." />
                            <ColHeader label="Drill" align="center" info="Open the full list of this influencer's auto-reply setups and see exactly which Instagram users interacted with each one." />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                        ) : users.length === 0 ? (
                            <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>No users with automations.</TableCell></TableRow>
                        ) : users.slice(usersPage * usersRpp, usersPage * usersRpp + usersRpp).map(u => {
                            const tooltipContent = (
                                <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" display="block" fontWeight={600}>{u.influencer_name || '— no name —'}</Typography>
                                    <Typography variant="caption" display="block">ID: <span style={{ fontFamily: 'monospace' }}>#{u.influencer_id}</span></Typography>
                                    {u.instagram_username && <Typography variant="caption" display="block">IG: @{u.instagram_username} (click to open profile)</Typography>}
                                    {u.influencer_email && <Typography variant="caption" display="block">Email: {u.influencer_email}</Typography>}
                                    {u.influencer_phone && <Typography variant="caption" display="block">Phone: {u.influencer_phone}</Typography>}
                                </Box>
                            );
                            return (
                            <TableRow key={u.influencer_id} hover>
                                <TableCell>
                                    <Tooltip title={tooltipContent} arrow placement="right">
                                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', cursor: 'help', maxWidth: 200 }}>
                                            {u.influencer_name
                                                ? <Typography variant="body2" fontWeight={500} noWrap>{u.influencer_name}</Typography>
                                                : <IgUsernameLink username={u.instagram_username} fallback={`#${u.influencer_id}`} variant="body2" bold />
                                            }
                                            {u.instagram_username && u.influencer_name && (
                                                <IgUsernameLink username={u.instagram_username} variant="caption" bold={false} />
                                            )}
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">{fmtNum(u.automation_count)}</TableCell>
                                <TableCell align="right">{fmtNum(u.post_count)}</TableCell>
                                <TableCell align="right">{fmtNum(u.story_count)}</TableCell>
                                <TableCell align="right">{fmtNum(u.live_count)}</TableCell>
                                <TableCell align="right">{fmtNum(u.auto_reply_count)}</TableCell>
                                <TableCell align="right">{fmtNum(u.zap_link_count)}</TableCell>
                                <TableCell align="right">{fmtNum(u.total_dms_sent)}</TableCell>
                                <TableCell align="right">{fmtNum(u.total_opens)}</TableCell>
                                <TableCell align="right">{fmtNum(u.zap_clicks)}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="See all auto-reply setups for this influencer and exactly who interacted with each one">
                                        <IconButton size="small" onClick={() => openUserAutomations(u.influencer_id, u.influencer_name, u.instagram_username)}>
                                            <VisibilityOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {users.length > 0 && (
                    <TablePagination
                        component="div"
                        count={users.length}
                        page={usersPage}
                        onPageChange={(_, p) => setUsersPage(p)}
                        rowsPerPage={usersRpp}
                        onRowsPerPageChange={(e) => { setUsersRpp(parseInt(e.target.value, 10)); setUsersPage(0); }}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    />
                )}
            </TableContainer>

            {/* ================= MODAL 1: User Automations ================= */}
            {/* ================= Storefront / Link-in-Bio detail ================= */}
            <Dialog open={presenceModal.open} onClose={closePresenceModal} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Storefront &amp; Link in Bio — {presenceModal.name || `#${presenceModal.influencer_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {presenceModal.username ? `@${presenceModal.username} · ` : ''}
                            {startDate && endDate
                                ? `${fmtDate(startDate)} to ${fmtDate(endDate)}`
                                : startDate ? `From ${fmtDate(startDate)}`
                                : endDate ? `Up to ${fmtDate(endDate)}`
                                : 'All-time'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {presenceLoading ? (
                        <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
                    ) : (!presenceDetail.storefront && !presenceDetail.bio) ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                            This influencer hasn't created a storefront or a Link-in-Bio page.
                        </Typography>
                    ) : (
                        <>
                            {/* ---- Storefront ---- */}
                            {presenceDetail.storefront && (
                                <Box mb={3}>
                                    <SectionHeader
                                        title="Storefront"
                                        info="Traffic for this influencer's shoppable storefront in the selected date range."
                                    />
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                        <a href={storefrontUrl(presenceDetail.storefront.store_slug) || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                            {storefrontUrl(presenceDetail.storefront.store_slug)}
                                        </a>
                                    </Typography>
                                    <Grid container spacing={2} mb={1}>
                                        {[
                                            ['Views', presenceDetail.storefront.total_views],
                                            ['Visitors', presenceDetail.storefront.unique_visitors],
                                            ['Clicks', presenceDetail.storefront.total_clicks],
                                            ['CTR', `${presenceDetail.storefront.ctr}%`],
                                        ].map(([label, val]) => (
                                            <Grid item xs={6} md={3} key={String(label)}>
                                                <Card variant="outlined">
                                                    <CardContent sx={{ pb: '12px !important' }}>
                                                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                        <Typography variant="h6" fontWeight={700}>
                                                            {typeof val === 'number' ? fmtNum(val) : val}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ background: '#f5f5f5' }}>
                                                    <ColHeader label="Product" info="Products listed in this storefront." />
                                                    <ColHeader label="Clicks" align="right" info="Outbound clicks on this product in the selected date range." />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(presenceDetail.storefront.per_product || []).length === 0 ? (
                                                    <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>No products listed.</TableCell></TableRow>
                                                ) : presenceDetail.storefront.per_product.map((pr: any) => (
                                                    <TableRow key={pr.product_id} hover>
                                                        <TableCell>
                                                            {pr.product_url ? (
                                                                <a href={pr.product_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                                                                    {pr.product_title || `#${pr.product_id}`}
                                                                </a>
                                                            ) : (pr.product_title || `#${pr.product_id}`)}
                                                        </TableCell>
                                                        <TableCell align="right">{fmtNum(pr.clicks)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}

                            {/* ---- Link in Bio ---- */}
                            {presenceDetail.bio && (
                                <Box>
                                    <SectionHeader
                                        title="Link in Bio"
                                        info="Traffic for this influencer's Link-in-Bio page in the selected date range."
                                    />
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                        <a href={bioUrl(presenceDetail.bio.slug) || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                            {bioUrl(presenceDetail.bio.slug)}
                                        </a>
                                    </Typography>
                                    <Grid container spacing={2} mb={1}>
                                        {[
                                            ['Views', presenceDetail.bio.total_views],
                                            ['Visitors', presenceDetail.bio.unique_visitors],
                                            ['Clicks', presenceDetail.bio.total_clicks],
                                            ['CTR', `${presenceDetail.bio.ctr}%`],
                                        ].map(([label, val]) => (
                                            <Grid item xs={6} md={3} key={String(label)}>
                                                <Card variant="outlined">
                                                    <CardContent sx={{ pb: '12px !important' }}>
                                                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                        <Typography variant="h6" fontWeight={700}>
                                                            {typeof val === 'number' ? fmtNum(val) : val}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ background: '#f5f5f5' }}>
                                                    <ColHeader label="Link" info="Links on this Link-in-Bio page." />
                                                    <ColHeader label="Clicks" align="right" info="Outbound clicks on this link in the selected date range." />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(presenceDetail.bio.per_block || []).length === 0 ? (
                                                    <TableRow><TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>No links on this page.</TableCell></TableRow>
                                                ) : presenceDetail.bio.per_block.map((bl: any) => (
                                                    <TableRow key={bl.block_id} hover>
                                                        <TableCell>
                                                            {bl.url ? (
                                                                <a href={bl.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'none' }}>
                                                                    {bl.title || `#${bl.block_id}`}
                                                                </a>
                                                            ) : (bl.title || `#${bl.block_id}`)}
                                                        </TableCell>
                                                        <TableCell align="right">{fmtNum(bl.clicks)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={closePresenceModal}>Close</Button></DialogActions>
            </Dialog>

            <Dialog open={userModal.open} onClose={() => setUserModal({ open: false, influencer_id: null, name: null, username: null })} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={600} component="span">Automations for</Typography>
                        {userModal.name && (
                            <Typography variant="h6" fontWeight={600} component="span">{userModal.name}</Typography>
                        )}
                        {userModal.username && (
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                                <IgUsernameLink username={userModal.username} variant="body2" bold />
                            </Box>
                        )}
                        <Typography variant="body2" color="text.secondary" component="span" sx={{ fontFamily: 'monospace' }}>
                            (#{userModal.influencer_id})
                        </Typography>
                        <Tooltip title="Each card is one auto-reply setup. Inside, you'll see its keyword responses with stats for the selected date range. Click 'View Tracking' on any setup to see the exact Instagram users who interacted with it." arrow>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                    <IconButton size="small" onClick={() => setUserModal({ open: false, influencer_id: null, name: null, username: null })}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {userModalLoading ? (
                        <Box textAlign="center" py={4}><CircularProgress /></Box>
                    ) : userAutomations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" py={4} textAlign="center">No automations found.</Typography>
                    ) : userAutomations.map(a => (
                        <Card key={a.ig_automation_id} variant="outlined" sx={{ mb: 1.5 }}>
                            <CardContent sx={{ pb: '12px !important' }}>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} gap={2}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body1" fontWeight={600}>{a.automation_name || `Automation #${a.ig_automation_id}`}</Typography>
                                        <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                                            <Chip label={a.automation_media_type} size="small" variant="outlined" />
                                            <Chip label={a.status} size="small" color={a.status === 'Live' ? 'success' : 'default'} variant="outlined" />
                                            <Typography variant="caption" color="text.secondary">Total trigger count (lifetime): {fmtNum(a.trigger_count)}</Typography>
                                        </Box>
                                        {/* Linked posts / reels / stories — clickable thumbnails */}
                                        {Array.isArray(a.posts) && a.posts.length > 0 && (
                                            <Box display="flex" gap={0.75} mt={1.5} flexWrap="wrap" alignItems="center">
                                                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Linked content:</Typography>
                                                {a.posts.slice(0, 12).map(p => (
                                                    <PostThumbnail
                                                        key={p.ig_automation_post_id}
                                                        post={{
                                                            post_thumbnail_url: p.thumbnail_url,
                                                            post_media_url: p.media_url,
                                                            post_gcp_url: p.gcp_media_file_url,
                                                            post_permalink: p.permalink,
                                                            post_media_product_type: p.media_product_type,
                                                            post_shortcode: p.shortcode,
                                                        }}
                                                        size={56}
                                                    />
                                                ))}
                                                {a.posts.length > 12 && (
                                                    <Typography variant="caption" color="text.secondary">+{a.posts.length - 12} more</Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                    <Button size="small" variant="outlined" startIcon={<ChevronRightIcon />} onClick={() => openAutomationTracking(a.ig_automation_id, a.automation_name)} sx={{ flexShrink: 0 }}>View Tracking</Button>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ background: '#fafafa' }}>
                                            <ColHeader label="Rule" info="A reference number for this keyword response (used by us internally)." />
                                            <ColHeader label="Type" info="What kind of reply this is: Auto Reply (plain text message and/or a public comment reply) or Zap Link (a shoppable product carousel)." />
                                            <ColHeader label="Status" info="Whether this keyword response is currently active. Live = active and able to fire. Deleted = removed by the influencer (historical data still shown). Expired = inactive because the Instagram connection is broken." />
                                            <ColHeader label="Keywords" info="The trigger words people need to comment for this response to fire. Hover the value to see the full list if it gets cut off." />
                                            <ColHeader label="Triggers" align="right" info="How many times this keyword response fired in the date range — i.e. how many people commented with a matching keyword and we tried to reply." />
                                            <ColHeader label="Opens" align="right" info="Of those messages, how many the recipient actually opened and read." />
                                            <ColHeader label="Clicks" align="right" info="How many times someone clicked a product in the carousel. Only applies to Zap Link responses — shows '—' for plain text Auto Reply responses." />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {a.rules.length === 0 ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 2, color: 'text.secondary' }}>No rules configured for this automation</TableCell></TableRow>
                                        ) : a.rules.map(r => {
                                            const isZap = r.rule_type === 'zap_link';
                                            // Use whichever source has a non-zero count (handles edge cases where
                                            // the two trigger tables briefly disagree).
                                            const triggers = isZap ? Math.max(Number(r.zap_triggers) || 0, Number(r.dm_triggers) || 0) : Number(r.dm_triggers) || 0;
                                            const opens    = isZap ? Math.max(Number(r.zap_opens)    || 0, Number(r.dm_opens)    || 0) : Number(r.dm_opens)    || 0;
                                            return (
                                            <TableRow key={r.ig_automation_rule_id} sx={{ opacity: r.status === 'Deleted' ? 0.6 : 1 }}>
                                                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>#{r.ig_automation_rule_id}</Typography></TableCell>
                                                <TableCell><Chip label={isZap ? 'Zap Link' : 'Auto Reply'} size="small" color={isZap ? 'warning' : 'primary'} variant="outlined" /></TableCell>
                                                <TableCell><Chip label={r.status} size="small" color={r.status === 'Live' ? 'success' : r.status === 'Deleted' ? 'default' : 'warning'} variant="outlined" /></TableCell>
                                                <TableCell>
                                                    {r.keywords ? (
                                                        <Tooltip
                                                            title={
                                                                <Box sx={{ p: 0.5 }}>
                                                                    <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                                                                        All keywords ({r.keywords.split(',').length})
                                                                    </Typography>
                                                                    {r.keywords.split(',').map((kw, i) => (
                                                                        <Typography key={i} variant="caption" display="block">• {kw.trim()}</Typography>
                                                                    ))}
                                                                </Box>
                                                            }
                                                            arrow placement="top"
                                                        >
                                                            <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: 'block', cursor: 'help', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>{r.keywords}</Typography>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: 'block' }}>—</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">{fmtNum(triggers)}</TableCell>
                                                <TableCell align="right">{fmtNum(opens)}</TableCell>
                                                <TableCell align="right">
                                                    {isZap ? fmtNum(r.zap_clicks) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    ))}
                </DialogContent>
                <DialogActions><Button onClick={() => setUserModal({ open: false, influencer_id: null, name: null, username: null })}>Close</Button></DialogActions>
            </Dialog>

            {/* ================= MODAL 2: Automation Tracking (IG users + their events) ================= */}
            <Dialog open={trackingModal.open} onClose={() => setTrackingModal({ open: false, ig_automation_id: null, automation_name: '' })} maxWidth="xxl" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" fontWeight={600}>Interaction Tracking — {trackingModal.automation_name || `Automation   #${trackingModal.ig_automation_id}`}</Typography>
                        <Tooltip title="Every comment this setup replied to in the date range — one row per comment. The 'Auto-Reply' column shows the plain-text DM/reply outcome and the 'Zap Link' column shows the product-carousel outcome. Expand a row to read the message sent and/or see the full catalogue with this user's clicks." arrow>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                    <IconButton size="small" onClick={() => setTrackingModal({ open: false, ig_automation_id: null, automation_name: '' })}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {trackingLoading ? (
                        <Box textAlign="center" py={4}><CircularProgress /></Box>
                    ) : (
                        <>
                            {/* Unified Activity — auto-reply (DM) + zap-link (catalogue) merged per comment */}
                            <SectionHeader title={`Activity — ${mergedActivity.length} interaction${mergedActivity.length === 1 ? '' : 's'}`} info="One row per user comment. The auto-reply (comment reply / DM text) and the Zap Link product carousel for the SAME comment are combined here. Expand a row to see the message that was sent and/or the full catalogue with this user's clicks." />
                            {mergedActivity.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No activity for this automation in the date range.</Typography>
                            ) : (
                                <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ background: '#f5f5f5' }}>
                                                <TableCell width={36} />
                                                <ColHeader label="Post" info="Thumbnail of the Instagram post, reel, or story that the user commented on. Click to open it on Instagram in a new tab." />
                                                <ColHeader label="IG User" info="The Instagram user who commented. Click their @handle to open their profile in a new tab." />
                                                <ColHeader label="Keyword" info="The keyword in their comment that matched and triggered the automation." />
                                                <ColHeader label="Event" info="Where they commented from — Comments on a post, Story Replies, or Live Comments." />
                                                <ColHeader label="Triggered At" info="When this comment came in and the automation fired. For a merged row this is the comment's time (the auto-reply and the Zap Link send happen within the same moment)." />
                                                <ColHeader label="Opened At" info="When the user opened the DM. If both an auto-reply DM and a catalogue DM were sent, this shows the first one opened. Blank if not opened yet." />
                                                <ColHeader label="Auto-Reply" align="center" info="The plain-text reply outcome only (kept separate from the product carousel in the Zap Link column). 'DM Sent' = a text DM was delivered. 'Reply' = only a public comment reply, no DM. '—' = no auto-reply for this comment." />
                                                <ColHeader label="Zap Link" align="right" info="The product-carousel outcome (separate from the Auto-Reply column): delivery status plus how many products were sent and how many this user clicked. '—' = no catalogue was sent for this comment." />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {mergedActivity.slice(activityPage * activityRpp, activityPage * activityRpp + activityRpp).map(row => {
                                                const d = row.dm;
                                                const z = row.zap;
                                                const expanded = expandedActivityKey === row.key;
                                                const dmHasContent = Boolean(d && (
                                                    (d.comment_text && d.comment_text.trim()) ||
                                                    (d.reply_text && d.reply_text.trim()) ||
                                                    (d.message_text && d.message_text.trim())
                                                ));
                                                const hasExpand = dmHasContent || Boolean(z);

                                                // Catalogue (products sent) merged with this user's clicks, for the zap side.
                                                const catalogue = z && Array.isArray(z.catalogue) ? z.catalogue : [];
                                                const clicksByLink: Record<string, ZapClickDetail[]> = {};
                                                (z?.clicks || []).forEach(c => {
                                                    const k = String(c.ig_zap_link_id);
                                                    (clicksByLink[k] = clicksByLink[k] || []).push(c);
                                                });
                                                const catalogueIds = new Set(catalogue.map(i => Number(i.ig_zap_link_id)));
                                                const extraClicked = Object.keys(clicksByLink)
                                                    .filter(id => !catalogueIds.has(Number(id)))
                                                    .map(id => {
                                                        const c0 = clicksByLink[id][0];
                                                        return {
                                                            ig_zap_link_id: Number(id),
                                                            product_name: c0.product_name, brand_name: c0.brand_name, short_link_code: c0.short_link_code,
                                                            product_image_url: c0.product_image_url, manual_product_image_url: c0.manual_product_image_url, product_url: c0.product_url,
                                                            price: null, discount_text: null, _removed: true,
                                                        } as CatalogueItem & { _removed?: boolean };
                                                    });
                                                const catalogueRows = [...catalogue, ...extraClicked];

                                                return (
                                                <React.Fragment key={row.key}>
                                                <TableRow hover>
                                                    <TableCell>
                                                        {hasExpand ? (
                                                            <Tooltip title="Show the message sent and/or the catalogue & clicks" arrow>
                                                                <IconButton size="small" onClick={() => setExpandedActivityKey(expanded ? null : row.key)}>
                                                                    {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell>
                                                        {(row.post?.post_thumbnail_url || row.post?.post_media_url || row.post?.post_gcp_url || row.post?.post_permalink)
                                                            ? <PostThumbnail post={row.post} size={44} />
                                                            : <Typography variant="caption" color="text.disabled">—</Typography>
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        <IgUsernameLink username={row.ig_username} fallback="—" variant="body2" bold />
                                                        <Typography variant="caption" color="text.secondary" display="block">id: {row.ig_user_id}</Typography>
                                                    </TableCell>
                                                    <TableCell><Typography variant="caption">{row.keyword || '—'}</Typography></TableCell>
                                                    <TableCell><Chip label={EVENT_TYPE_LABEL[row.event_type] || row.event_type} size="small" variant="outlined" /></TableCell>
                                                    <TableCell><Typography variant="caption">{fmtDateTime(row.triggered_at)}</Typography></TableCell>
                                                    <TableCell><Typography variant="caption" sx={{ color: row.opened_at ? '#2e7d32' : 'text.disabled' }}>{fmtDateTime(row.opened_at)}</Typography></TableCell>
                                                    <TableCell align="center">
                                                        {!d ? <Typography variant="caption" color="text.disabled">—</Typography>
                                                            : d.message_id ? <Chip label="DM Sent" size="small" color="success" variant="outlined" />
                                                            : (d.reply_text && d.reply_text.trim()) ? <Chip label="Reply" size="small" variant="outlined" />
                                                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {!z ? <Typography variant="caption" color="text.disabled">—</Typography> : (
                                                            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.3}>
                                                                <Chip label={z.meta_api_status} size="small" color={z.meta_api_status === 'sent' ? 'success' : z.meta_api_status === 'failed' ? 'error' : 'warning'} variant="outlined" />
                                                                <Typography variant="caption" color="text.secondary">{fmtNum(z.links_sent_count)} sent / {fmtNum(z.click_count)} clicks</Typography>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                                                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, background: '#fafafa' }}>
                                                                {/* Auto-reply message blocks */}
                                                                {dmHasContent && d && (
                                                                    <Box mb={z ? 2 : 0}>
                                                                        <Typography variant="caption" fontWeight={600} display="block" mb={1}>Auto-Reply message</Typography>
                                                                        {d.comment_text && d.comment_text.trim() !== '' && (
                                                                            <Box sx={{ border: '1px solid #bbdefb', borderRadius: 1, p: 1.5, mb: 1 }}>
                                                                                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                                                    <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                                                                                    <Typography variant="caption" sx={{ color: '#1976d2' }}>Comment</Typography>
                                                                                </Box>
                                                                                <Typography variant="body2">{d.comment_text}</Typography>
                                                                            </Box>
                                                                        )}
                                                                        {d.reply_text && d.reply_text.trim() !== '' && (
                                                                            <Box sx={{ border: '1px solid #bbdefb', borderRadius: 1, p: 1.5, mb: 1 }}>
                                                                                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                                                    <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                                                                                    <Typography variant="caption" sx={{ color: '#1976d2' }}>Comment Reply</Typography>
                                                                                </Box>
                                                                                <Typography variant="body2">{d.reply_text}</Typography>
                                                                            </Box>
                                                                        )}
                                                                        {d.message_text && d.message_text.trim() !== '' && (
                                                                            <Box sx={{ border: '1px solid #e1bee7', borderRadius: 1, p: 1.5 }}>
                                                                                <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                                                                                    <SendOutlinedIcon sx={{ fontSize: 14, color: '#6a1b9a' }} />
                                                                                    <Typography variant="caption" sx={{ color: '#6a1b9a' }}>DM Sent</Typography>
                                                                                </Box>
                                                                                <Typography variant="body2">{d.message_text}</Typography>
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                                {/* Catalogue & clicks (zap side) */}
                                                                {z && (
                                                                    <>
                                                                        <Typography variant="caption" fontWeight={600} display="block" mb={1}>
                                                                            Catalogue sent &amp; clicks ({catalogueRows.length} product{catalogueRows.length === 1 ? '' : 's'} · {fmtNum(z.click_count)} click{Number(z.click_count) === 1 ? '' : 's'} by this user)
                                                                        </Typography>
                                                                        {catalogueRows.length === 0 ? (
                                                                            <Typography variant="caption" color="text.secondary">No catalogue products found for this send.</Typography>
                                                                        ) : (
                                                                            <Table size="small">
                                                                                <TableHead>
                                                                                    <TableRow>
                                                                                        <ColHeader label="Product" info="A product in the carousel that was sent to this user. Click the thumbnail or name to open it in a new tab." />
                                                                                        <ColHeader label="Brand" info="The brand or website where the product is sold." />
                                                                                        <ColHeader label="Short Code" info="Our tracking link ID. Click to open the product page in a new tab." />
                                                                                        <ColHeader label="Price" info="Product price / discount captured when the carousel was built." />
                                                                                        <ColHeader label="Clicks" align="right" info="How many times THIS user clicked this product. 0 means it was sent but not clicked." />
                                                                                        <ColHeader label="Last Clicked At" info="The most recent time this user clicked this product. '—' if never clicked." />
                                                                                        <ColHeader label="Device" info="Device / OS / browser of this user's most recent click on this product." />
                                                                                    </TableRow>
                                                                                </TableHead>
                                                                                <TableBody>
                                                                                    {catalogueRows.map((item: any) => {
                                                                                        const itemClicks = clicksByLink[String(item.ig_zap_link_id)] || [];
                                                                                        const clickCount = itemClicks.length;
                                                                                        const lastClick = itemClicks[0];
                                                                                        const shortHref = shortLinkUrl(item.short_link_code);
                                                                                        return (
                                                                                        <TableRow key={item.ig_zap_link_id} sx={{ opacity: item._removed ? 0.7 : 1 }}>
                                                                                            <TableCell>
                                                                                                <ProductLink product={item} nameMaxWidth={240} avatarSize={28} />
                                                                                                {item._removed && <Typography variant="caption" color="text.disabled" display="block">(removed from catalogue)</Typography>}
                                                                                            </TableCell>
                                                                                            <TableCell><Typography variant="caption" color="text.secondary">{item.brand_name || '—'}</Typography></TableCell>
                                                                                            <TableCell>
                                                                                                {shortHref ? (
                                                                                                    <Tooltip title={`Open ${shortHref} in new tab`} arrow>
                                                                                                        <a href={shortHref} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                                                                                                            <Chip label={item.short_link_code} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 10, cursor: 'pointer' }} clickable />
                                                                                                        </a>
                                                                                                    </Tooltip>
                                                                                                ) : (
                                                                                                    <Chip label={item.short_link_code || '—'} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 10 }} />
                                                                                                )}
                                                                                            </TableCell>
                                                                                            <TableCell>
                                                                                                <Box display="flex" gap={0.5} alignItems="center">
                                                                                                    {item.price ? <Typography variant="caption" color="text.secondary">{item.price}</Typography> : <Typography variant="caption" color="text.disabled">—</Typography>}
                                                                                                    {item.discount_text && <Chip label={item.discount_text} size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: 9 }} />}
                                                                                                </Box>
                                                                                            </TableCell>
                                                                                            <TableCell align="right">
                                                                                                <Typography variant="body2" fontWeight={600} sx={{ color: clickCount > 0 ? '#2e7d32' : 'text.disabled' }}>{fmtNum(clickCount)}</Typography>
                                                                                            </TableCell>
                                                                                            <TableCell><Typography variant="caption" sx={{ color: lastClick ? 'inherit' : 'text.disabled' }}>{lastClick ? fmtDateTime(lastClick.clicked_at) : '—'}</Typography></TableCell>
                                                                                            <TableCell><Typography variant="caption" color={lastClick ? 'inherit' : 'text.disabled'}>{lastClick ? `${lastClick.device_type}${lastClick.os ? ` · ${lastClick.os}` : ''}${lastClick.browser ? ` · ${lastClick.browser}` : ''}` : '—'}</Typography></TableCell>
                                                                                        </TableRow>
                                                                                        );
                                                                                    })}
                                                                                </TableBody>
                                                                            </Table>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                                </React.Fragment>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={mergedActivity.length}
                                        page={activityPage}
                                        onPageChange={(_, p) => setActivityPage(p)}
                                        rowsPerPage={activityRpp}
                                        onRowsPerPageChange={(e) => { setActivityRpp(parseInt(e.target.value, 10)); setActivityPage(0); }}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                    />
                                </TableContainer>
                            )}

                        </>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setTrackingModal({ open: false, ig_automation_id: null, automation_name: '' })}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export { ZapFlowTrackingDashboard };
